import React from 'react';
import { cn } from '@lqa-frontend/ui-system';
import { Icon } from '../icon';
import type { IconSize } from '../icon';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES — Status Badge
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Semantic color states for the status badge.
 *
 * Each state maps to a distinct background / border / text color palette
 * defined in the Figma design system:
 *
 * | State     | Use case                                    |
 * |-----------|---------------------------------------------|
 * | standard  | Default / informational (blue)              |
 * | neutral   | Inactive / not started (grey)               |
 * | success   | Positive outcome — approved, complete (green)|
 * | warning   | Needs attention — pending review (orange)   |
 * | danger    | Negative outcome — denied, error (red)      |
 */
export type BadgeState =
	'standard' | 'neutral' | 'success' | 'warning' | 'danger';

/**
 * Shape controls the border-radius:
 * - `default`  → 6px rounded rectangle
 * - `rounded`  → fully rounded pill (border-radius: 999px)
 */
export type BadgeShape = 'default' | 'rounded';

/**
 * Size controls height, font size, font weight, and icon slot dimensions:
 * - `default` → 24px tall, 14px/400, 16px icon slots
 * - `small`   → 20px tall, 12px/600 (semi-bold), 14px icon slots
 */
export type BadgeSize = 'default' | 'small';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES — Score Badge
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Valid score values for the score badge.
 *
 * Scores map to a 5-point color scale (red → green). `null` represents "N/A".
 * Each score has its own light and bold color palette defined in Figma.
 */
export type BadgeScore = 1 | 2 | 3 | 4 | 5 | null;

/**
 * Visual intensity for the score badge:
 * - `light` → pastel background with colored border and dark text
 * - `bold`  → solid saturated background with white text, no border
 */
export type BadgeScoreStyle = 'light' | 'bold';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES — Discriminated Union Props
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Props for the `status` variant of the Badge component.
 *
 * Status badges display a semantic label (e.g. "Approved", "Denied") inside
 * a colored container. They support two shapes, two sizes, and optional
 * leading/trailing icon slots plus a dismiss action.
 */
type IStatusVariantProps = {
	/** Selects the status badge variant. */
	variant: 'status';

	/** Semantic color state — determines background, border, and text colors. */
	state: BadgeState;

	/** Text displayed inside the badge. */
	label: string;

	/**
	 * Border-radius style.
	 * @default 'default'
	 */
	shape?: BadgeShape;

	/**
	 * Controls height, font size, and font weight.
	 * @default 'default'
	 */
	size?: BadgeSize;

	/**
	 * Optional icon rendered before the label.
	 * Slot is 16px (default size) or 14px (small size).
	 */
	leadingIcon?: React.ReactNode;

	/**
	 * Optional icon rendered after the label.
	 * Slot is 16px (default size) or 14px (small size).
	 */
	trailingIcon?: React.ReactNode;

	/**
	 * When provided, a dismiss (×) button is rendered as the last element.
	 * Clicking it calls this handler.
	 */
	onDismiss?: () => void;
};

/**
 * Props for the `score` variant — full layout with a text label.
 *
 * Renders a 24px-tall badge showing the score and label text
 * (e.g. "3.00 - Quality Score"). Supports `light` and `bold` styles.
 */
type IScoreWithLabelProps = {
	/** Selects the score badge variant. */
	variant: 'score';

	/**
	 * Numeric score value. Pass `null` for "N/A".
	 * Determines the color palette from the 5-point scale.
	 */
	score: BadgeScore;

	/** Label text rendered inside the badge (e.g. "3.00 - Quality Score"). */
	label: string;

	/**
	 * Visual intensity.
	 * - `light`: pastel background, colored border, dark text
	 * - `bold`: solid background, white text, no border
	 * @default 'light'
	 */
	style?: BadgeScoreStyle;
};

/**
 * Props for the `score` variant — compact layout (score only).
 *
 * Renders a 20×20px square showing just the score number (or "N/A").
 * Always uses the `light` style per Figma spec — `style` is not
 * accepted to prevent silent no-ops.
 */
type IScoreCompactProps = {
	/** Selects the score badge variant. */
	variant: 'score';

	/**
	 * Numeric score value. Pass `null` for "N/A".
	 * Determines the color palette from the 5-point scale.
	 */
	score: BadgeScore;

	/** Omit label for the compact 20×20 score-only layout. */
	label?: undefined;
};

/** Shared HTML attributes accepted by every Badge variant. */
interface IBadgeCommonProps {
	/** Additional CSS classes merged onto the root element. */
	'className'?: string;
	/** Test identifier for automated testing. */
	'data-testid'?: string;
}

/**
 * Union of all Badge variant props plus shared HTML attributes.
 *
 * Usage with discriminated union ensures TypeScript enforces that only
 * relevant props are passed for each variant:
 *
 * ```tsx
 * <Badge variant="status" state="success" label="Approved" />
 * <Badge variant="score" score={3} label="3.00 - Quality" />
 * <Badge variant="score" score={5} />  // compact score-only
 * ```
 */
export type IBadgeProps = (
	IStatusVariantProps | IScoreWithLabelProps | IScoreCompactProps
) &
	IBadgeCommonProps;

// ─────────────────────────────────────────────────────────────────────────────
// STYLE TOKENS — Status Badge
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Color palettes for each status badge state.
 *
 * Colors are taken directly from the Figma design spec. Each key maps to
 * a Tailwind class string controlling background, border, and text color.
 */
export const STATUS_STATE_STYLES: Record<BadgeState, string> = {
	standard: 'border-outline-interactive bg-interactive-light text-text-link',
	neutral: 'border-divider bg-base-2 text-text-secondary',
	success: 'border-outline-success bg-success-light text-text-success',
	warning: 'border-outline-warning bg-warning-light text-text-warning',
	danger: 'border-outline-danger bg-danger-light text-text-danger',
};

/**
 * Shape styles controlling border-radius and min-width.
 *
 * The `rounded` shape includes `min-w-24` per the Figma spec to ensure
 * pill badges maintain a minimum circular appearance.
 */
const STATUS_SHAPE_STYLES: Record<BadgeShape, string> = {
	default: 'rounded-s',
	rounded: 'rounded-max min-w-24',
};

/**
 * Size styles controlling height, typography, and icon slot dimensions.
 *
 * - `default`: 24px height, Body M (14px/400 weight/140% line-height)
 * - `small`: 20px height, Body S Semi-Bold (12px/600 weight/16px line-height)
 *
 * Note: Font weight is determined by size, NOT by shape. This matches the
 * Figma spec where small badges always use semi-bold regardless of shape.
 */
const STATUS_SIZE_STYLES: Record<BadgeSize, string> = {
	default: 'h-24 text-14 font-normal leading-large',
	small: 'h-20 text-12 font-semibold leading-lh6',
};

/** Icon slot dimensions per badge size. */
const STATUS_ICON_SIZE: Record<BadgeSize, string> = {
	default: 'size-16',
	small: 'size-14',
};

/** Dismiss-icon size token per badge size. */
const DISMISS_ICON_SIZE: Record<BadgeSize, IconSize> = {
	default: 's',
	small: 'xs',
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLE TOKENS — Score Badge
// ─────────────────────────────────────────────────────────────────────────────

/** Type-safe keys for the score color lookup. */
type ScoreKey = '1' | '2' | '3' | '4' | '5' | 'na';

/** Lookup key used when no score is available. */
const SCORE_NA_KEY: ScoreKey = 'na';

/** Maps each numeric score to its lookup key. */
const SCORE_KEYS: Record<NonNullable<BadgeScore>, ScoreKey> = {
	1: '1',
	2: '2',
	3: '3',
	4: '4',
	5: '5',
};

/**
 * Color palettes for each score value, keyed by score (or 'na').
 *
 * The scale progresses from red (1 = worst) through orange (2), yellow (3),
 * olive (4), to green (5 = best). `null` maps to a neutral grey.
 *
 * Each entry is a flat Tailwind class string (same format as STATUS_STATE_STYLES)
 * for both `light` and `bold` visual styles:
 * - `light`: pastel bg, colored border, dark text
 * - `bold`: solid saturated bg, white text (no border — handled at render time)
 */
const SCORE_COLORS: Record<ScoreKey, { light: string; bold: string }> = {
	'na': {
		light: 'bg-base-2 border-outline-neutral text-zinc-600',
		bold: 'bg-neutral',
	} /* N/A — neutral grey */,
	'1': {
		light: 'bg-score-light-1 border-outline-score-1 text-text-score-1',
		bold: 'bg-score-1',
	} /* Score 1 — red (danger) */,
	'2': {
		light: 'bg-score-light-2 border-outline-score-2 text-text-score-2',
		bold: 'bg-score-2',
	} /* Score 2 — orange (warning) */,
	'3': {
		light: 'bg-score-light-3 border-outline-score-3 text-text-score-3',
		bold: 'bg-score-3',
	} /* Score 3 — yellow (caution) */,
	'4': {
		light: 'bg-score-light-4 border-outline-score-4 text-text-score-4',
		bold: 'bg-score-4',
	} /* Score 4 — olive (good) */,
	'5': {
		light: 'bg-score-light-5 border-outline-score-5 text-text-score-5',
		bold: 'bg-score-5',
	} /* Score 5 — green (excellent) */,
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/** Resolves a numeric score (or null) to its lookup key and display label. */
function resolveScore(score: BadgeScore) {
	if (score === null) {
		return { key: SCORE_NA_KEY, compactLabel: 'N/A', isNA: true };
	}

	return { key: SCORE_KEYS[score], compactLabel: String(score), isNA: false };
}

interface IIconSlotProps {
	children: React.ReactNode;
	size: BadgeSize;
}

/** Renders an icon inside a size-aware slot container. */
function IconSlot({ children, size }: IIconSlotProps) {
	return (
		<span
			className={cn(
				'inline-flex shrink-0 items-center justify-center',
				STATUS_ICON_SIZE[size]
			)}
		>
			{children}
		</span>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `Badge` — A unified badge component for the LQA Design System.
 *
 * Renders either a **status badge** or a **score badge** depending on the
 * `variant` prop. TypeScript's discriminated union ensures only the relevant
 * props are accepted for each variant.
 *
 * ## Status Badge (`variant="status"`)
 *
 * Displays a semantic label inside a colored container. Supports:
 * - 5 color states: `standard`, `neutral`, `success`, `warning`, `danger`
 * - 2 shapes: `default` (rounded rectangle) and `rounded` (pill)
 * - 2 sizes: `default` (24px) and `small` (20px)
 * - Optional leading/trailing icons and dismiss button
 *
 * ```tsx
 * <Badge variant="status" state="success" label="Approved" />
 * <Badge variant="status" state="danger" label="Denied" shape="rounded" size="small" />
 * ```
 *
 * ## Score Badge (`variant="score"`)
 *
 * Displays a numeric quality score (1–5) or "N/A" on a red-to-green
 * color scale. Two rendering modes:
 * - **With text**: provide `label` for a full-width badge (e.g. "3.00 - Quality")
 * - **Score only**: omit `label` for a compact 20×20px square
 *
 * Two visual styles (with-text only):
 * - `light`: pastel background with border
 * - `bold`: solid saturated background with white text
 *
 * ```tsx
 * <Badge variant="score" score={4} label="4.00 - Quality" />
 * <Badge variant="score" score={5} style="bold" />
 * <Badge variant="score" score={null} />  // renders "N/A"
 * ```
 */
export function Badge(props: IBadgeProps) {
	const { className, 'data-testid': testId } = props;

	if (props.variant === 'status') {
		return (
			<StatusBadgeContent
				{...props}
				className={className}
				data-testid={testId}
			/>
		);
	}

	if (props.label !== undefined) {
		return (
			<ScoreWithTextContent
				{...props}
				label={props.label}
				className={className}
				data-testid={testId}
			/>
		);
	}

	return (
		<ScoreCompactContent
			{...props}
			className={className}
			data-testid={testId}
		/>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// VARIANT RENDERERS
// ─────────────────────────────────────────────────────────────────────────────

/** Status badge — semantic label with color state, shape, size, and optional icons. */
function StatusBadgeContent({
	state,
	label,
	shape = 'default',
	size = 'default',
	leadingIcon,
	trailingIcon,
	onDismiss,
	className,
	'data-testid': testId,
}: IStatusVariantProps & IBadgeCommonProps) {
	return (
		<span
			data-slot="badge"
			data-testid={testId}
			className={cn(
				'inline-flex items-center justify-center gap-4 border px-8 py-2',
				STATUS_STATE_STYLES[state],
				STATUS_SHAPE_STYLES[shape],
				STATUS_SIZE_STYLES[size],
				className
			)}
		>
			{leadingIcon && <IconSlot size={size}>{leadingIcon}</IconSlot>}
			<span className="inline-flex items-center text-center">{label}</span>
			{trailingIcon && <IconSlot size={size}>{trailingIcon}</IconSlot>}
			{onDismiss && (
				<button
					type="button"
					onClick={onDismiss}
					aria-label="Dismiss"
					className={cn(
						'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-xxs border-none bg-transparent p-0',
						'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1',
						STATUS_ICON_SIZE[size]
					)}
				>
					<Icon name="x" size={DISMISS_ICON_SIZE[size]} />
				</button>
			)}
		</span>
	);
}

/** Score badge — full layout with text label, supports light and bold styles. */
function ScoreWithTextContent({
	score,
	label,
	style = 'light',
	className,
	'data-testid': testId,
}: IScoreWithLabelProps & IBadgeCommonProps) {
	const { key } = resolveScore(score);
	const colors = SCORE_COLORS[key];
	const isBold = style === 'bold';

	return (
		<span
			data-slot="badge"
			data-testid={testId}
			className={cn(
				'inline-flex h-24 items-center justify-center gap-10 rounded-s px-8 py-2',
				'text-14 font-normal leading-large',
				isBold ? cn(colors.bold, 'text-white') : cn('border', colors.light),
				className
			)}
		>
			<span className="inline-flex items-center text-center">{label}</span>
		</span>
	);
}

/**
 * Score badge — compact 20×20px square showing just the score number.
 * Always uses light style per Figma spec.
 */
function ScoreCompactContent({
	score,
	className,
	'data-testid': testId,
}: IScoreCompactProps & IBadgeCommonProps) {
	const { key, compactLabel, isNA } = resolveScore(score);
	const colors = SCORE_COLORS[key];

	return (
		<span
			data-slot="badge"
			data-testid={testId}
			className={cn(
				'inline-flex size-20 items-center justify-center rounded-xs border px-2 py-0',
				colors.light,
				className
			)}
		>
			<span
				className={cn(
					'inline-flex items-center text-center',
					isNA
						? 'text-8 font-bold leading-large'
						: 'text-14 font-semibold leading-large'
				)}
			>
				{compactLabel}
			</span>
		</span>
	);
}
